// The signed-in player's own play, money and orders, at every café they have dealt with (spec/08 §4.2, spec/15 §2;
// F-MOB-P-21, ADR-0054). What the player web shows instead of "coming soon".
//
//   GET  /v1/me/wallets                                   a wallet per organisation: cash, bonus, total
//   GET  /v1/me/wallets/{organizationId}/statement        one wallet's movements, newest first, paginated
//   GET  /v1/me/passes                                    the passes held: active ones, then the recent past
//   GET  /v1/me/play-sessions                             play history across every café, newest first, paginated
//   GET  /v1/me/play-sessions/current                     the live session, if any: where, time left, what pays
//   POST /v1/me/play-sessions/current/lock                ask the PC to lock (202: it locks when the PC does)
//   POST /v1/me/play-sessions/current/unlock              ask it to unlock (the same)
//   POST /v1/me/play-sessions/current/end                 end the session (log out of the PC)
//   POST /v1/me/play-sessions/current/passes/{holdingId}/pin   use a queued pass next
//   POST /v1/me/play-sessions/current/calls               call staff to the PC
//   POST /v1/me/play-sessions/current/calls/cancel        cancel that call
//   GET  /v1/me/orders                                    orders across every café, newest first, paginated
//   GET  /v1/me/orders/{saleId}                           one order with its receipt
//   GET  /v1/me/venues/{venueId}/passes                   the passes the player can buy at a venue, and their wallet there
//   POST /v1/me/venues/{venueId}/passes/{productId}/purchase   buy one, from that wallet or paid online ⟳
//   POST /v1/me/venues/{venueId}/wallet/online-top-ups    top the wallet at that café up, paid online ⟳
//   GET  /v1/me/payments/{paymentIntentId}                one of the player's own online payments, to poll
//
// **Online payment from the web** (owner, 2026-10-12, ADR-0061): where the café takes online payments (a provider is
// configured, its payee at the provider is active, the venue has turned online payments on), a player may top their
// wallet up and buy a pass online. The answer carries a payment: the player opens its `checkoutUrl` (the provider's
// hosted checkout: UPI, cards, netbanking) and the web polls it. Nothing is credited or issued until the provider says it
// was paid; then the wallet is credited (the café's best top-up extra applied, as at the kiosk) or the pass issued.
//
// **About the caller only.** A better-auth session is required and nothing else: every route reads the player behind
// the session, and nothing in a request can name another. An organisation, an order or a holding that is not the
// caller's answers the same `404` as one that does not exist. The routes take no permission: these are a person's own
// facts (spec/07 §6.3). The writes act on the caller's own running session exactly as the PC's kiosk would, and are
// audited as the player's (`sessions.lock_requested` and `sessions.lock` when the PC confirms, the same for unlock,
// `sessions.ended`, `sessions.pass_pinned`; a pass bought, `passes.web_purchase.created` and `.applied`, with the sale's
// and the wallet's own rows).
//
// **Lock and unlock follow the PC** (2026-10-11): the phone asks, the PC is sent `session.lock` / `session.unlock`, and
// the session changes only when the PC acks it. Until then the live session carries `lockRequest`, pending; a PC that
// refused or did not answer before the command expired reads `failed`.
//
// **No time is added from the phone** (owner, 2026-10-11): choosing the wallet for play time stays on the PC's kiosk.
// **Passes are bought from the wallet** (owner, 2026-10-11): the wallet at that café is the only tender from the web (no
// pay at the counter, no online payment), the same kiosk sale and pass the PC sells. Where the wallet holds too little,
// the phone says to top up at the counter.
//
// **Money** is integer minor units with the currency, as everywhere; amounts are the server's, never a client's.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { OrganizationIdSchema, PassHoldingIdSchema, PassProductIdSchema, PassPurchaseIdSchema, PaymentIntentIdSchema, SaleIdSchema, SessionIdSchema, StationIdSchema, VenueIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { SESSION_PIN_MAX_LENGTH, SESSION_PIN_MIN_LENGTH, SESSION_PIN_PATTERN } from "../primitives/session-pin.js";
import { RaiseCallSchema, StationCallSchema } from "./calls.js";
import { IdempotentCreateHeaderSchema } from "./id-checks.js";
import { PassHoldingStatusSchema, StationPassSchema } from "./passes.js";
import { SessionEndReasonSchema, SessionSchema, SessionStatusSchema } from "./sessions.js";
import { WalletTransactionSchema } from "./wallet.js";
const SIGNED_IN = 'Needs a signed-in player: a session cookie in a browser, `Authorization: Bearer` in the apps. About the caller only.';
// ---------------------------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------------------------
/** An organisation (a café brand) the player has data at, by name. */
export const MyOrganizationRefSchema = z
    .object({ id: OrganizationIdSchema, name: z.string() })
    .openapi('MyOrganizationRef', { description: 'An organisation the player has dealt with.' });
/** A venue, by name; `slug` links to its public page when it is listed. */
export const MyVenueRefSchema = z
    .object({
    id: VenueIdSchema,
    name: z.string(),
    /** The venue's public page slug, or `null` when it is not publicly listed. */
    slug: z.string().nullable(),
})
    .openapi('MyVenueRef', { description: 'A venue the player has dealt with.' });
// ---------------------------------------------------------------------------------------------
// Wallets (F-WAL-01, F-WAL-08)
// ---------------------------------------------------------------------------------------------
export const MyWalletSchema = z
    .object({
    organization: MyOrganizationRefSchema,
    /** Money the player paid in; what a refund gives back. */
    cash: NonNegativeMoneySchema,
    /** Bonus from a top-up offer; spent first, never refunded. */
    bonus: NonNegativeMoneySchema,
    total: NonNegativeMoneySchema,
    /** "Use my wallet for play time": whether this wallet may pay for play once other time runs out (F-WAL-04). */
    useForPlay: z.boolean(),
})
    .openapi('MyWallet', { description: 'The player’s wallet at one organisation, spendable at its venues.' });
export const MyWalletListSchema = z
    .object({ data: z.array(MyWalletSchema) })
    .openapi('MyWalletList', { description: 'A wallet per organisation where the player has one, by organisation name.' });
export const MyWalletStatementSchema = paginated(WalletTransactionSchema, 'MyWalletStatement');
const MyWalletParamsSchema = z.object({ organizationId: OrganizationIdSchema }).openapi('MyWalletParams');
export const listMyWalletsRoute = createRoute({
    method: 'get',
    path: '/v1/me/wallets',
    operationId: 'listMyWallets',
    tags: ['me'],
    summary: 'Your wallets',
    description: `${SIGNED_IN} One wallet per organisation the player has topped up at, with its cash, bonus and total now.`,
    responses: {
        200: jsonResponse(MyWalletListSchema, 'The wallets.'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMyWalletStatementRoute = createRoute({
    method: 'get',
    path: '/v1/me/wallets/{organizationId}/statement',
    operationId: 'getMyWalletStatement',
    tags: ['me'],
    summary: 'Your wallet’s statement at one organisation',
    description: `${SIGNED_IN} Every top-up, bonus, spend, refund and adjustment, newest first. \`404\` for an organisation where the player has no wallet.`,
    request: { params: MyWalletParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(MyWalletStatementSchema, 'A page of the statement.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No wallet of yours at this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Passes (F-PRC-02; spec/15 P-14)
// ---------------------------------------------------------------------------------------------
/** Where a held pass stands with the live session: running now, waiting its turn, or neither. */
export const MyPassPlaceSchema = z
    .enum(['in_use', 'queued', 'held'])
    .openapi('MyPassPlace', { description: '`in_use` funds the live session now, `queued` waits on it, `held` is not on it.' });
export const MyPassSchema = z
    .object({
    id: PassHoldingIdSchema,
    organization: MyOrganizationRefSchema,
    /** The venue it was sold at; absent when it is gone. */
    venue: MyVenueRefSchema.optional(),
    name: z.string(),
    grantedSeconds: z.int().min(0),
    remainingSeconds: z.int().min(0),
    price: NonNegativeMoneySchema,
    status: PassHoldingStatusSchema,
    place: MyPassPlaceSchema,
    purchasedAt: IsoDateTimeSchema,
    firstUsedAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    /** The venues it can be used at by name; `null` for every venue of the organisation. */
    usableAt: z.array(z.string()).nullable(),
})
    .openapi('MyPass', { description: 'A pass the player holds.' });
export const MyPassListSchema = z
    .object({
    /** Usable passes: active with time left, soonest to expire first. */
    active: z.array(MyPassSchema),
    /** The 50 most recent others (played through, expired, refunded), newest first. */
    past: z.array(MyPassSchema),
})
    .openapi('MyPassList', { description: 'The passes the player holds, across every organisation.' });
export const listMyPassesRoute = createRoute({
    method: 'get',
    path: '/v1/me/passes',
    operationId: 'listMyPasses',
    tags: ['me'],
    summary: 'Your passes',
    description: `${SIGNED_IN} Active passes with their time left, and where each stands with the live session; then the recent past.`,
    responses: {
        200: jsonResponse(MyPassListSchema, 'The passes.'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Play sessions (spec/15 P-01, P-10)
// ---------------------------------------------------------------------------------------------
export const MyPlaySessionEntrySchema = z
    .object({
    id: SessionIdSchema,
    organization: MyOrganizationRefSchema,
    venue: MyVenueRefSchema,
    /** "PC 07", or the station's own name. */
    stationName: z.string(),
    status: SessionStatusSchema,
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema.nullable(),
    /** Seconds played so far. */
    playedSeconds: z.int().min(0),
    endReason: SessionEndReasonSchema.nullable(),
})
    .openapi('MyPlaySessionEntry', { description: 'One visit: a session the player played.' });
export const MyPlaySessionListSchema = paginated(MyPlaySessionEntrySchema, 'MyPlaySessionList');
/** A lock or unlock asked for from the phone that the PC has not yet done. */
export const MyLockRequestSchema = z
    .object({
    action: z.enum(['lock', 'unlock']),
    /** `pending` until the PC answers; `failed` when it refused, or did not answer before the request expired. */
    state: z.enum(['pending', 'failed']),
    requestedAt: IsoDateTimeSchema,
})
    .openapi('MyLockRequest');
export const MyLiveSessionSchema = z
    .object({
    /** The session with its time left, funding breakdown and the server's clock to count against. */
    session: SessionSchema,
    organization: MyOrganizationRefSchema,
    venue: MyVenueRefSchema,
    station: z.object({ id: StationIdSchema, name: z.string() }).openapi('MyLiveSessionStation'),
    /** The open call for help from this PC; absent when there is none. */
    call: StationCallSchema.optional(),
    /** Lock and unlock from the phone are offered: a PC session (not a console bay staff run). */
    remoteLock: z.boolean(),
    /** The lock or unlock asked for from the phone that the PC has not done yet; absent when there is none. */
    lockRequest: MyLockRequestSchema.optional(),
})
    .openapi('MyLiveSession', { description: 'The player’s running session, where it is and what pays for it.' });
export const MyCurrentPlaySessionSchema = z
    .object({
    /** Absent when the player is not playing. */
    live: MyLiveSessionSchema.optional(),
    serverTime: IsoDateTimeSchema,
})
    .openapi('MyCurrentPlaySession', { description: 'The live session, absent when the player is not playing.' });
export const listMyPlaySessionsRoute = createRoute({
    method: 'get',
    path: '/v1/me/play-sessions',
    operationId: 'listMyPlaySessions',
    tags: ['me'],
    summary: 'Your play history',
    description: `${SIGNED_IN} Every session the player played, at every café, newest first. (\`/v1/me/sessions\` is where you are signed in.)`,
    request: { query: PageQuerySchema },
    responses: {
        200: jsonResponse(MyPlaySessionListSchema, 'A page of sessions.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMyCurrentPlaySessionRoute = createRoute({
    method: 'get',
    path: '/v1/me/play-sessions/current',
    operationId: 'getMyCurrentPlaySession',
    tags: ['me'],
    summary: 'Your live session',
    description: `${SIGNED_IN} The session running now, if any: the venue and PC, time left against the server's clock, what pays for it, and the open call.`,
    responses: {
        200: jsonResponse(MyCurrentPlaySessionSchema, 'The live session, or none.'),
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
const LIVE_ACTION_ERRORS = {
    401: commonProblemResponses[401],
    404: problemResponse('You are not playing right now.', 'not-found'),
    409: problemResponse('The session has already ended, or it is run by staff at the counter.', 'session-already-ended', 'conflict'),
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
};
export const LockMyPlaySessionSchema = z
    .object({
    /** A PIN to unlock with at the PC, set on the session's first lock only (spec/05 §6 rule 1). */
    pin: z
        .string()
        .regex(SESSION_PIN_PATTERN, `A PIN is ${SESSION_PIN_MIN_LENGTH}–${SESSION_PIN_MAX_LENGTH} digits.`)
        .optional(),
})
    .strict()
    .openapi('LockMyPlaySession');
export const lockMyPlaySessionRoute = createRoute({
    method: 'post',
    path: '/v1/me/play-sessions/current/lock',
    operationId: 'lockMyPlaySession',
    tags: ['me'],
    summary: 'Lock your PC',
    description: `${SIGNED_IN} Asks the PC the session runs on to lock its screen, as a step-away lock would: the PC is sent ` +
        '`session.lock`, and the session locks only when the PC confirms it (the games keep running; billing goes on unless ' +
        'the venue pauses it while locked). Until then the live session carries `lockRequest` pending, and `failed` if the ' +
        'PC refused or did not answer in time. A session already locked is answered as it is (200). Audited as asked, and ' +
        'again as locked when it lands.',
    request: { body: { content: { 'application/json': { schema: LockMyPlaySessionSchema } }, required: false } },
    responses: {
        200: jsonResponse(MyLiveSessionSchema, 'The live session, already locked: nothing was sent.'),
        202: jsonResponse(MyLiveSessionSchema, 'The PC was asked to lock; the live session carries the pending request.'),
        400: commonProblemResponses[400],
        ...LIVE_ACTION_ERRORS,
    },
});
export const unlockMyPlaySessionRoute = createRoute({
    method: 'post',
    path: '/v1/me/play-sessions/current/unlock',
    operationId: 'unlockMyPlaySession',
    tags: ['me'],
    summary: 'Unlock your PC',
    description: `${SIGNED_IN} Asks the PC to unlock: the signed-in account is the proof, as a phone unlock at the kiosk is. The PC is ` +
        'sent `session.unlock` and the session unlocks only when the PC confirms it; until then `lockRequest` is pending. A ' +
        'session that is not locked is answered as it is (200). Audited as asked, and again as unlocked when it lands.',
    responses: {
        200: jsonResponse(MyLiveSessionSchema, 'The live session, not locked: nothing was sent.'),
        202: jsonResponse(MyLiveSessionSchema, 'The PC was asked to unlock; the live session carries the pending request.'),
        ...LIVE_ACTION_ERRORS,
    },
});
export const endMyPlaySessionRoute = createRoute({
    method: 'post',
    path: '/v1/me/play-sessions/current/end',
    operationId: 'endMyPlaySession',
    tags: ['me'],
    summary: 'End your session',
    description: `${SIGNED_IN} Logs out of the PC and settles the session, as Log out at the PC does. Audited.`,
    responses: { 200: jsonResponse(MyPlaySessionEntrySchema, 'The session, ended.'), ...LIVE_ACTION_ERRORS },
});
const MyPassPinParamsSchema = z.object({ holdingId: PassHoldingIdSchema }).openapi('MyPassPinParams');
export const pinPassOnMyPlaySessionRoute = createRoute({
    method: 'post',
    path: '/v1/me/play-sessions/current/passes/{holdingId}/pin',
    operationId: 'pinPassOnMyPlaySession',
    tags: ['me'],
    summary: 'Use a queued pass next',
    description: `${SIGNED_IN} Moves one of the passes queued on the live session to the front, as at the kiosk. Audited.`,
    request: { params: MyPassPinParamsSchema },
    responses: {
        200: jsonResponse(MyLiveSessionSchema, 'The live session with the new order.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('You are not playing right now, or that pass is not yours.', 'not-found'),
        409: problemResponse('That pass is not queued on the session.', 'pass-not-queued', 'session-already-ended'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const callStaffFromMyPlaySessionRoute = createRoute({
    method: 'post',
    path: '/v1/me/play-sessions/current/calls',
    operationId: 'callStaffFromMyPlaySession',
    tags: ['me'],
    summary: 'Call staff to your PC',
    description: `${SIGNED_IN} Raises a call for help from the PC the session runs on, as the kiosk's Call staff does; a previous open call is replaced.`,
    request: { body: { content: { 'application/json': { schema: RaiseCallSchema } }, required: true } },
    responses: {
        201: jsonResponse(StationCallSchema, 'The call.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('You are not playing right now.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelMyStaffCallRoute = createRoute({
    method: 'post',
    path: '/v1/me/play-sessions/current/calls/cancel',
    operationId: 'cancelMyStaffCall',
    tags: ['me'],
    summary: 'Cancel your call for help',
    description: `${SIGNED_IN} Cancels the open call from the PC the session runs on.`,
    responses: {
        200: jsonResponse(StationCallSchema, 'The call, cancelled.'),
        401: commonProblemResponses[401],
        404: problemResponse('You are not playing, or there is no open call.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Orders and receipts (F-POS-13; spec/15 P-17)
// ---------------------------------------------------------------------------------------------
export const MyOrderStatusSchema = z
    .enum(['placed', 'partially_paid', 'paid', 'partially_refunded', 'refunded', 'cancelled'])
    .openapi('MyOrderStatus', { description: '`placed` and `partially_paid` wait for payment at the counter.' });
export const MyOrderSummarySchema = z
    .object({
    id: SaleIdSchema,
    organization: MyOrganizationRefSchema,
    venue: MyVenueRefSchema,
    /** The counter's order number for the day ("#0421"); null for an older sale. */
    orderNumber: z.int().min(1).nullable(),
    status: MyOrderStatusSchema,
    /** "Coke ×2, 1 hour". */
    title: z.string(),
    itemCount: z.int().min(0),
    total: MoneySchema,
    outstanding: MoneySchema,
    refunded: MoneySchema,
    /** The receipt's number ("BILL/2026-27/000021") once one is issued. */
    documentNumber: z.string().nullable(),
    placedAt: IsoDateTimeSchema,
    paidAt: IsoDateTimeSchema.nullable(),
})
    .openapi('MyOrderSummary', { description: 'One order in the player’s history.' });
export const MyOrderListSchema = paginated(MyOrderSummarySchema, 'MyOrderList');
export const MyOrderLineSchema = z
    .object({
    name: z.string(),
    quantity: z.int().min(1),
    unitPrice: NonNegativeMoneySchema,
    total: MoneySchema,
    modifiers: z.array(z.string()),
})
    .openapi('MyOrderLine');
/**
 * One row of the receipt's tenders, by the one rule every receipt follows (ADR-0052): a tender and what it applied;
 * the cash handed over; the change handed back; or the change added to a wallet. `nested` rows sit under a split
 * tender's own cash row.
 */
export const MyReceiptTenderRowSchema = z
    .object({
    kind: z.enum(['tender', 'cash_received', 'change', 'change_to_wallet']),
    /** For `tender`: `cash`, `counter_card`, `counter_upi`, `wallet`, `on_account`, `online`. */
    tender: z.string().nullable(),
    amount: NonNegativeMoneySchema,
    nested: z.boolean(),
})
    .openapi('MyReceiptTenderRow');
export const MyReceiptDocumentSchema = z
    .object({
    kind: z.enum(['bill', 'bill_of_supply', 'tax_invoice']),
    formattedNumber: z.string(),
    issuedAt: IsoDateTimeSchema,
    /** The venue's GSTIN, on a bill of supply or a tax invoice. */
    gstin: z.string().nullable(),
    taxable: MoneySchema,
    /** GST by component and rate, on a tax invoice. */
    taxComponents: z.array(z.object({ component: z.string(), rateBps: z.int().min(0), amount: MoneySchema }).openapi('MyReceiptTaxComponent')),
})
    .openapi('MyReceiptDocument', { description: 'The bill, bill of supply or tax invoice the order was issued.' });
export const MyOrderSchema = z.object({
    ...MyOrderSummarySchema.shape,
    /** The venue's clock, for showing times as the receipt does. */
    venueTimezone: z.string(),
    lines: z.array(MyOrderLineSchema),
    /** What the lines add up to before offers and discounts. */
    gross: MoneySchema,
    promotions: z.array(z.object({ name: z.string(), amount: NonNegativeMoneySchema }).openapi('MyOrderPromotion')),
    discounts: z.array(z.object({ code: z.string().nullable(), amount: NonNegativeMoneySchema }).openapi('MyOrderDiscount')),
    tax: MoneySchema,
    paid: MoneySchema,
    tenders: z.array(MyReceiptTenderRowSchema),
    refunds: z.array(z
        .object({ amount: NonNegativeMoneySchema, tender: z.string(), status: z.enum(['pending_approval', 'succeeded', 'declined']), at: IsoDateTimeSchema })
        .openapi('MyOrderRefund')),
    /** The issued bill, bill of supply or tax invoice; absent until one is issued (an unpaid order). */
    document: MyReceiptDocumentSchema.optional(),
}).openapi('MyOrder', { description: 'One order with its receipt: lines, totals, how it was paid and any change.' });
const MyOrderParamsSchema = z.object({ saleId: SaleIdSchema }).openapi('MyOrderParams');
export const listMyOrdersRoute = createRoute({
    method: 'get',
    path: '/v1/me/orders',
    operationId: 'listMyOrders',
    tags: ['me'],
    summary: 'Your orders',
    description: `${SIGNED_IN} Every order placed for the player at any café (counter or kiosk), newest first.`,
    request: { query: PageQuerySchema },
    responses: {
        200: jsonResponse(MyOrderListSchema, 'A page of orders.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getMyOrderRoute = createRoute({
    method: 'get',
    path: '/v1/me/orders/{saleId}',
    operationId: 'getMyOrder',
    tags: ['me'],
    summary: 'One of your orders, with its receipt',
    description: `${SIGNED_IN} The lines, totals, tenders with cash received and change, refunds and the issued document, as the emailed receipt shows them.`,
    request: { params: MyOrderParamsSchema },
    responses: {
        200: jsonResponse(MyOrderSchema, 'The order.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such order of yours.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// Passes bought from the wallet (F-MOB-P-21, the kiosk's F-KIO-14 sale; spec/15 P-06, P-10)
// ---------------------------------------------------------------------------------------------
//
// The owner's decision of 2026-10-11: a player may buy a pass from the web, paid from their wallet at that café and by
// nothing else (no pay at the counter, no online payment). It needs no payment provider: the wallet was topped up at the
// counter. The passes are the ones a player can already buy themselves, at the PC's kiosk; the sale is the same
// ordinary sale the kiosk makes (on the `web` channel), priced by the server from the product's live version; bought
// while the player is playing at that venue on a PC the pass is for, it joins the session as at the kiosk, otherwise it
// waits in the player's passes.
/** A pass the player can buy at a venue, with what it costs them now and whether their wallet there covers it. */
export const MyShopPassSchema = z
    .object({
    ...StationPassSchema.shape,
    /** What it costs the player now: the price with any offer the café applies by itself. What the purchase charges. */
    total: NonNegativeMoneySchema,
    /** The wallet here holds at least `total` now. */
    affordable: z.boolean(),
    /** The player is playing at this venue on a PC the pass is for: bought now, it joins that session. */
    forYourPc: z.boolean(),
})
    .openapi('MyShopPass', { description: 'A pass the player can buy at a venue from their wallet there.' });
/**
 * What a player may top their wallet up with online at a café (ADR-0061): the amounts the café's kiosk offers as tiles
 * and the limits a typed amount is held to. The café's best extra for the amount is added when the payment captures.
 */
export const MyTopUpOptionsSchema = z
    .object({
    min: NonNegativeMoneySchema,
    max: NonNegativeMoneySchema,
    /** The amounts offered as one-tap choices, cheapest first (the kiosk's tiles). */
    presets: z.array(NonNegativeMoneySchema),
    /** The café's top-up extras: each tier's threshold and the bonus it adds (F-WAL-03). */
    offers: z.array(z.object({ minTopup: NonNegativeMoneySchema, bonus: NonNegativeMoneySchema }).openapi('MyTopUpOffer')),
})
    .openapi('MyTopUpOptions', { description: 'How a player may top their wallet up online at a café.' });
export const MyVenuePassShopSchema = z
    .object({
    venue: MyVenueRefSchema,
    organization: MyOrganizationRefSchema,
    /** The café takes its wallet. When `false` nothing can be bought here from the wallet. */
    walletEnabled: z.boolean(),
    /** The player's wallet at this café, cash and bonus together; zero when they have none. */
    walletBalance: NonNegativeMoneySchema,
    /**
     * The café takes online payments now (ADR-0061): a provider is configured, its payee there is active and the venue
     * has turned online payments on. When `true` a pass may be bought, and the wallet topped up, online.
     */
    onlinePayments: z.boolean(),
    /** Topping the wallet up online here: present only with `onlinePayments` and the wallet on. */
    topUp: MyTopUpOptionsSchema.nullable(),
    /**
     * The passes on sale to players here (the ones the PC's kiosk sells), cheapest first. Empty when neither the wallet
     * nor online payments are open here.
     */
    passes: z.array(MyShopPassSchema),
})
    .openapi('MyVenuePassShop', { description: 'What the player can buy at a venue, from their wallet there or online.' });
const MyVenuePassShopParamsSchema = z.object({ venueId: VenueIdSchema }).openapi('MyVenuePassShopParams');
export const getMyVenuePassShopRoute = createRoute({
    method: 'get',
    path: '/v1/me/venues/{venueId}/passes',
    operationId: 'getMyVenuePassShop',
    tags: ['me'],
    summary: 'The passes you can buy at a venue from your wallet',
    description: `${SIGNED_IN} The passes a player can buy themselves at this venue (the ones the PC's kiosk sells), each with what ` +
        'it costs the player now and whether their wallet there covers it, and that wallet’s balance. `404` for a venue ' +
        'at no organisation the player has dealt with, as for one that does not exist: they hold no wallet there.',
    request: { params: MyVenuePassShopParamsSchema },
    responses: {
        200: jsonResponse(MyVenuePassShopSchema, 'The passes and the wallet.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No wallet of yours at this venue, or no such venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
/**
 * One of the player's own online payments (ADR-0061), as the web shows and polls it: open `checkoutUrl` to pay; the
 * status moves to `succeeded` only when the provider says it was paid.
 */
export const MyPaymentSchema = z
    .object({
    id: PaymentIntentIdSchema,
    /** A wallet top-up, or a sale (a pass bought online). */
    purpose: z.enum(['sale', 'wallet_topup']),
    status: z.enum(['pending', 'succeeded', 'failed', 'cancelled', 'expired']),
    amount: NonNegativeMoneySchema,
    /** The provider's checkout, where the player pays: UPI, cards, netbanking. */
    checkoutUrl: z.string().min(1).max(2000),
    expiresAt: IsoDateTimeSchema,
    capturedAt: IsoDateTimeSchema.nullable(),
    venue: MyVenueRefSchema,
    organization: MyOrganizationRefSchema,
})
    .openapi('MyPayment', { description: 'One of the player’s own online payments (ADR-0061).' });
export const PurchaseMyPassSchema = z
    .object({
    /**
     * How it is paid: the player's wallet at this café, or `online` through the payment provider where the café takes
     * online payments (`onlinePayments` on the shop). Online, the pass is issued once the provider says it was paid.
     */
    tender: z.enum(['wallet', 'online']),
    /**
     * The total the screen showed, compared and never used as a price: when the server's total differs nothing is
     * bought and the answer is `409 price-changed` with the fresh `total`.
     */
    expectedTotal: MoneySchema.optional(),
})
    .strict()
    .openapi('PurchaseMyPass', { description: 'Buy a pass, paid from the wallet at this café or online.' });
export const MyPassPurchaseSchema = z
    .object({
    id: PassPurchaseIdSchema,
    productId: PassProductIdSchema,
    /**
     * `applied`: bought and issued. `awaiting_payment`: bought online and waiting for the provider to say it was paid
     * (pay through `payment`). `cancelled` or `expired`: an online purchase that was not paid in time.
     */
    status: z.enum(['applied', 'awaiting_payment', 'cancelled', 'expired']),
    /** The pass bought, once issued: it is in the player's passes. Null while it waits for the online payment. */
    holdingId: PassHoldingIdSchema.nullable(),
    name: z.string(),
    durationSeconds: z.int().min(0),
    /** What the purchase costs (and, once applied, what was paid). */
    paid: NonNegativeMoneySchema,
    /** How it is paid. */
    tender: z.enum(['wallet', 'online']),
    /** The online payment to pay it with, while it waits; null for a wallet purchase. */
    payment: MyPaymentSchema.nullable(),
    /** The order it is, with its receipt, in the player's orders. */
    saleId: SaleIdSchema,
    /** Seconds it added to the session the player is playing here; 0 when it waits in their passes for a later visit. */
    queuedSeconds: z.int().min(0),
    /** The wallet here after the purchase. */
    walletBalance: NonNegativeMoneySchema,
    purchasedAt: IsoDateTimeSchema,
})
    .openapi('MyPassPurchase', { description: 'A pass the player bought from their wallet or online.' });
const MyPassPurchaseParamsSchema = z.object({ venueId: VenueIdSchema, productId: PassProductIdSchema }).openapi('MyPassPurchaseParams');
export const purchaseMyPassRoute = createRoute({
    method: 'post',
    path: '/v1/me/venues/{venueId}/passes/{productId}/purchase',
    operationId: 'purchaseMyPass',
    tags: ['me'],
    summary: 'Buy a pass from your wallet',
    description: `${SIGNED_IN} Buys one of the passes the venue sells to players, paid from the player's wallet at this café and by ` +
        'nothing else. The same sale the PC’s kiosk makes (on the `web` channel), priced from the product’s live version, ' +
        'paid at once; the pass is issued and, when the player is playing at this venue on a PC it is for, joins that ' +
        'session. `409 wallet-insufficient-funds` when the wallet holds too little (top up at the counter), `409 ' +
        'wallet-disabled` where the café does not take its wallet; either way nothing is written. A retry with the same ' +
        '`Idempotency-Key` answers the purchase already made. Audited as the player. **`tender: online`** (ADR-0061): the ' +
        'same sale, paid online through the payment provider where the café takes online payments (`409 ' +
        'online-payments-unavailable` otherwise): the answer is `awaiting_payment` with the `payment` to open; the pass is ' +
        'issued (and joins the session, as above) once the provider says it was paid, and the purchase lapses with the payment.',
    request: {
        params: MyPassPurchaseParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: PurchaseMyPassSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(MyPassPurchaseSchema, 'The pass, bought and paid from the wallet; or, online, waiting for its payment.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No wallet of yours at this venue, or no such pass there.', 'not-found'),
        409: problemResponse('The pass is not sold to players here, the café does not take its wallet or online payments, the wallet holds too little, or the price changed.', 'pass-not-buyable-here', 'pass-product-inactive', 'wallet-disabled', 'wallet-insufficient-funds', 'price-changed', 'online-payments-unavailable'),
        422: problemResponse('The same key was already used for a different purchase.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The payment provider could not be reached. Try again.', 'payment-provider-unavailable'),
    },
});
/** Top the wallet at this café up online: the cash to add. The server picks the café's best extra for the amount. */
export const MyOnlineTopUpSchema = z
    .object({ amount: NonNegativeMoneySchema })
    .strict()
    .openapi('MyOnlineTopUpRequest', { description: 'Top the wallet at a café up online (ADR-0061).' });
const MyVenueWalletParamsSchema = z.object({ venueId: VenueIdSchema }).openapi('MyVenueWalletParams');
export const beginMyOnlineTopUpRoute = createRoute({
    method: 'post',
    path: '/v1/me/venues/{venueId}/wallet/online-top-ups',
    operationId: 'beginMyOnlineTopUp',
    tags: ['me'],
    summary: 'Top your wallet up online at a café',
    description: `${SIGNED_IN} Checks the amount against the café's wallet limits, then asks the payment provider for a payment the ` +
        'player opens (`checkoutUrl`) and pays: UPI, cards, netbanking. **Nothing is credited here**: the wallet is credited ' +
        'once, with tender `online` and the café’s best extra for the amount, when the provider says it was paid (ADR-0061). ' +
        '`404` for a venue at no organisation the player has dealt with. `409 online-payments-unavailable` where the café does ' +
        'not take online payments, `409 wallet-disabled` or `wallet-limit` (outside the limits, or over the maximum balance). ' +
        'A retry with the same `Idempotency-Key` answers the same payment. Audited as the player.',
    request: {
        params: MyVenueWalletParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: MyOnlineTopUpSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(MyPaymentSchema, 'The payment to open and pay.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No wallet of yours at this venue, or no such venue.', 'not-found'),
        409: problemResponse('The café does not take online payments or its wallet, or the amount is outside its limits.', 'online-payments-unavailable', 'wallet-disabled', 'wallet-limit', 'wallet-topup-tax-at-sale'),
        422: problemResponse('The same key was already used for a different payment.', 'idempotency-key-reused'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The payment provider could not be reached. Try again.', 'payment-provider-unavailable'),
    },
});
const MyPaymentParamsSchema = z.object({ paymentIntentId: PaymentIntentIdSchema }).openapi('MyPaymentParams');
export const getMyPaymentRoute = createRoute({
    method: 'get',
    path: '/v1/me/payments/{paymentIntentId}',
    operationId: 'getMyPayment',
    tags: ['me'],
    summary: 'One of your online payments',
    description: `${SIGNED_IN} An online payment the player began on the web (a top-up or a pass), as the server last recorded it — ` +
        'poll it after paying. Another player’s, or one that does not exist, is the same `404`.',
    request: { params: MyPaymentParamsSchema },
    responses: {
        200: jsonResponse(MyPaymentSchema, 'The payment.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such payment of yours.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=me-player.js.map